import type { OverTarget } from "../../project/common/documentTarget.js";

/**
 * The ONE emit-time value conversion: an op's value must be in the DOCUMENT's
 * canonical vocabulary — the same forms the writer produces (captureOverValue)
 * and the evaluator applies (applyValueAtPath patches arrays in place via
 * fromArray). A raw property-row snapshot (e.g. a Color as its hex number)
 * gets ASSIGNED onto the receiver's field, replacing the value object.
 *
 * Canonical forms:
 * - primitives / null → as-is
 * - three value types (Color/Vector/Quaternion/Euler — anything with toArray)
 *   → numeric array; Euler's trailing string `order` is dropped (the
 *   evaluator's fromArray keeps the node's own order)
 * - plain arrays of primitives -> copied
 * - scene objects and textures -> a scoped document reference via the caller's
 *   encoder (the emission point has addressing; the save has its own)
 * - anything else (materials, unknown objects) -> NOT EXPRESSIBLE: the edit
 *   must not be emitted. Materials are captured by the assignment command.
 */
export type OpValueResult =
    | { readonly ok: true; readonly value: unknown }
    | { readonly ok: false; readonly reason: string };

export function serializeOpValue(
    value: unknown,
    options?: { encodeNodeReference?: (object: object) => OverTarget | null },
): OpValueResult {
    if (value === null || value === undefined) return { ok: true, value: value ?? null };
    switch (typeof value) {
        case "number":
        case "string":
        case "boolean":
            return { ok: true, value };
        case "object": {
            if ((value as { isObject3D?: boolean; isTexture?: boolean }).isObject3D === true
                || (value as { isTexture?: boolean }).isTexture === true) {
                const reference = options?.encodeNodeReference?.(value as object) ?? null;
                return reference
                    ? { ok: true, value: reference }
                    : { ok: false, reason: "the referenced object or resource has no address, so the edit cannot be stored" };
            }
            if ((value as { isMaterial?: boolean }).isMaterial === true) {
                return { ok: false, reason: "materials are recorded as material assignments, not property values" };
            }
            const toArray = (value as { toArray?: unknown }).toArray;
            if (typeof toArray === "function") {
                const array = (value as { toArray(): unknown[] }).toArray();
                if (Array.isArray(array)) {
                    // same rule as the writer: take the leading numeric run;
                    // exactly one trailing non-number (Euler's order) is fine
                    const nums: number[] = [];
                    for (const v of array) { if (typeof v === "number") nums.push(v); else break; }
                    if (nums.length > 0 && nums.length >= array.length - 1) return { ok: true, value: nums };
                }
                return { ok: false, reason: "the value could not be converted to numbers" };
            }
            if (Array.isArray(value) && value.every(v => typeof v === "number" || typeof v === "string" || typeof v === "boolean")) {
                return { ok: true, value: [...value] };
            }
            return { ok: false, reason: `a ${(value as { constructor?: { name?: string } }).constructor?.name ?? "object"} value cannot be stored or sent` };
        }
        default:
            return { ok: false, reason: `unsupported value type ${typeof value}` };
    }
}
